import _values from 'lodash/values';
import type { BlockId, BlockType, ComponentsUnionType } from '@unifyapps/defs/types/block';
import { useMemo } from 'react';
import { InterfacePageEntityComponentType } from '@unifyapps/defs/types/page';
import InterfacePageHelper from '../../helper/InterfacePage';
import { useInterfaceStore } from './context';
import { useGlobalStateStore } from '../GlobalStateStore';

export const useGetInterfaceStoreState = () => {
  return {
    getInterfaceStoreState: useInterfaceStore().getState,
  };
};

export const useBlocksInActiveInterfacePage = () => {
  const blocks = useBlocksFromPage();
  return useMemo(() => _values(blocks), [blocks]);
};

export const useBlockFromPage = <T = ComponentsUnionType>({ blockId }: { blockId: BlockId }) => {
  const baseBlock = useInterfaceStore().use.block.fromBase({ blockId });
  const { device } = useGlobalStateStore().use.deviceDetails();
  const deviceBlock = useInterfaceStore().use.block.fromDevice({ blockId, device });

  return (deviceBlock ?? baseBlock) as BlockType<T> | undefined;
};

export const useBlocksFromPage = () => {
  const baseBlocks = useInterfaceStore().use.blocks.fromBaseDevice();
  const { device } = useGlobalStateStore().use.deviceDetails();
  const deviceBlocks = useInterfaceStore().use.blocks.fromDevice({
    deviceVariant: device,
  });

  return useMemo(() => {
    // Most of the time deviceBlocks will be undefined, so we can return baseBlocks directly
    if (!deviceBlocks) {
      return baseBlocks ?? {};
    }

    return {
      ...baseBlocks,
      ...deviceBlocks,
    };
  }, [baseBlocks, deviceBlocks]);
};

export const useInterfaceModules = () => {
  const interfacePages = useInterfaceStore().use.pages();
  const activeInterfacePageId = useInterfaceStore().use.activeInterfacePageId();

  return useMemo(() => {
    return _values(interfacePages).filter(
      (interfacePage) =>
        InterfacePageHelper.getComponentType(interfacePage) ===
          InterfacePageEntityComponentType.MODULE && interfacePage.id !== activeInterfacePageId,
    );
  }, [activeInterfacePageId, interfacePages]);
};
